import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Mic } from "lucide-react";

import { AppProvider } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import Media from "@/pages/Media";
import Jobs from "@/pages/Jobs";
import Logs from "@/pages/Logs";
import StoragePage from "@/pages/StoragePage";
import Settings from "@/pages/Settings";
import AIAnalysis from "@/pages/AIAnalysis";
import People from "@/pages/People";
import References from "@/pages/References";
import Story from "@/pages/Story";
import Timeline from "@/pages/Timeline";
import Render from "@/pages/Render";
import AudioMix from "@/pages/AudioMix";
import PhaseModule from "@/pages/PhaseModule";
import ColorGrade from "@/pages/ColorGrade";
import Enhancement from "@/pages/Enhancement";
import BetaReport from "@/pages/BetaReport";
import ShareLook from "@/pages/ShareLook";
import FilmReview from "@/pages/FilmReview";
import Wizard from "@/pages/Wizard";
import Welcome, { WELCOME_FLAG } from "@/pages/Welcome";
import { useDesktop } from "@/hooks/useDesktop";

const PHASE = {
  audio: { icon: Mic, title: "External Audio", phase: 2,
    description: "Import lapel mics, DJ mixers and recorders, analyze waveforms, and synchronize external audio to multi-camera footage. (Clip-level audio analysis + sync already run from the AI Analysis page.)",
    planned: ["Waveform correlation sync", "Clipping/silence detection", "Manual offset correction", "Multi-cam alignment"],
    dependsOn: ["Waveforms", "Scan"] },
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#181B26", border: "1px solid #252A3B", color: "#F1F5F9" } }} />
        <Routes>
          <Route path="/share/:token" element={<ShareLook />} />
          <Route path="/film/:token" element={<FilmReview />} />
          <Route path="*" element={<Studio />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

function FirstRunRedirect() {
  const desktop = useDesktop();
  const loc = useLocation();
  if (desktop?.desktop && !localStorage.getItem(WELCOME_FLAG) && loc.pathname !== "/welcome") return <Navigate to="/welcome" replace />;
  return null;
}

function Studio() {
  return (
        <Layout>
          <FirstRunRedirect />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/wizard" element={<Wizard />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/media" element={<Media />} />
            <Route path="/ai-analysis" element={<AIAnalysis />} />
            <Route path="/people" element={<People />} />
            <Route path="/references" element={<References />} />
            <Route path="/story" element={<Story />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/render" element={<Render />} />
            <Route path="/audio-mix" element={<AudioMix />} />
            <Route path="/color" element={<ColorGrade />} />
            <Route path="/enhancement" element={<Enhancement />} />
            <Route path="/beta" element={<BetaReport />} />
            <Route path="/export" element={<Render />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/storage" element={<StoragePage />} />
            <Route path="/settings" element={<Settings />} />
            {Object.entries(PHASE).map(([path, cfg]) => (
              <Route key={path} path={`/${path}`} element={<PhaseModule {...cfg} />} />
            ))}
          </Routes>
        </Layout>
  );
}

export default App;
